import { Box, Container, Grid2 } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Footer } from '../../../components/footers/Footer';
import { HomeHeader } from '../../../components/new/headers/HomeHeader';
import { ContactUsPopup } from '../../../components/modals/ContactUsPopup';
import { backendHost } from '../../../providers/apis/blogApi';
import { PageMain } from '../../../components/seo/PageMain';
import { DetailPageHelmet } from '../helper/DetailPageHelmet';
import { getUsecaseDetail } from '../api/api';
import { getPublicSiteUrl } from '../siteUrl';

const USECASE_BODY_CLASS =
  'new-usecase-content w-full max-w-none text-left prose prose-lg prose-headings:text-[#021744] prose-p:text-[#42506a] prose-li:text-[#42506a]';

function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 320);
}

function getMediaSrc(mediaPath) {
  if (!mediaPath) return '';
  if (typeof mediaPath === 'string' && mediaPath.startsWith('http')) return mediaPath;
  return `${backendHost}${mediaPath}`;
}

function splitAreaTags(area) {
  if (!area || typeof area !== 'string') return [];
  return area
    .split(/[,;|]/)
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function MetaCard({ label, children }) {
  if (!children) return null;

  return (
    <Grid2 size={{ xs: 12, md: 4 }}>
      <Box className="h-full rounded-xl border border-[#E6EAF2] bg-white p-5 shadow-sm">
        <p className="mb-2 text-center text-base font-bold text-[#294b78]">{label}</p>
        <div className="text-sm text-[#1a2f55]">{children}</div>
      </Box>
    </Grid2>
  );
}

export function UsecaseDetail() {
  const { usecaseName: usecaseRelevance } = useParams();
  const [usecaseData, setUsecaseData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpenContact, setIsOpenContact] = useState(false);
  const productSectionRef = useRef(null);
  const bodySectionRef = useRef(null);

  const siteUrl = getPublicSiteUrl();
  const canonicalUrl = useMemo(() => {
    if (!siteUrl || !usecaseRelevance) return '';
    return `${siteUrl}/articles/usecase/${encodeURIComponent(usecaseRelevance)}`;
  }, [siteUrl, usecaseRelevance]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setUsecaseData(null);
    setLoading(true);

    (async () => {
      if (!usecaseRelevance) {
        if (!cancelled) {
          setLoadError('Use case not found');
          setLoading(false);
        }
        return;
      }

      try {
        const accolade = await getUsecaseDetail(usecaseRelevance);
        if (!cancelled) {
          if (!accolade) {
            setLoadError('Use case not found');
          } else {
            setUsecaseData(accolade);
          }
        }
      } catch {
        if (!cancelled) {
          setLoadError('Use case not found');
          setUsecaseData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [usecaseRelevance]);

  const pageTitle = usecaseData
    ? `${usecaseData.title || 'Use Case'} | Charlee.ai`
    : 'Use Case | Charlee.ai';
  const metaDescription = useMemo(() => {
    if (!usecaseData) return 'Charlee.ai use cases';
    return (
      stripHtml(usecaseData.Description || usecaseData.Body) ||
      usecaseData.title ||
      'Charlee.ai use case'
    );
  }, [usecaseData]);

  const scrollToSection = (sectionRef) => {
    if (!sectionRef?.current) return;
    const sectionTop = sectionRef.current.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: sectionTop - 110, behavior: 'smooth' });
  };

  const title = usecaseData?.title || 'Use Case';
  const description = usecaseData?.Description || '';
  const body = usecaseData?.Body || '';
  const hasHtmlBody = /<\/?[a-z][\s\S]*>/i.test(body);
  const ogImage = useMemo(() => getMediaSrc(usecaseData?.Graphic1), [usecaseData]);
  const graphicSrc = ogImage;
  const areaTags = splitAreaTags(usecaseData?.Area);

  const breadcrumbItems = useMemo(() => {
    if (!title || title === 'Use Case') return undefined;
    return [
      { name: 'Home', path: '/' },
      { name: 'Use cases', path: '/articles/usecases' },
      { name: title, url: canonicalUrl },
    ];
  }, [title, canonicalUrl]);

  return (
    <div className="w-full overflow-hidden bg-[#F9F9F9]">
      {!loading && (
        <DetailPageHelmet
          pageTitle={pageTitle}
          metaDescription={metaDescription}
          canonicalUrl={canonicalUrl}
          ogImage={ogImage}
          ogType="article"
          noIndex={Boolean(loadError)}
          articlePublishedTime={usecaseData?.ArticleDate || usecaseData?.LastUpdated}
          author={usecaseData?.Author}
          breadcrumbItems={breadcrumbItems}
        />
      )}

      <HomeHeader headerColor="#F9F9F9" textColor="#021744" />
      <PageMain className="usecase-page">
        {loading && (
          <div
            className="mt-24 flex min-h-[50vh] w-full items-center justify-center"
            role="status"
            aria-label="Loading"
          >
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#22c0b1] border-t-transparent" />
          </div>
        )}

        {!loading && loadError && (
          <div className="mt-32 w-full px-4 text-center">
            <p className="text-lg text-[#021744]">{loadError}</p>
            <Link to="/articles/usecases" className="mt-4 inline-block text-[#22c0b1] underline">
              Back to use cases
            </Link>
          </div>
        )}

        {!loading && usecaseData && (
          <>
            <article className="w-full py-10">
              <Container maxWidth="lg" className="pb-10 pt-6">
                <Box className="mb-8 overflow-hidden rounded-2xl border border-[#021744] bg-[#021744] shadow-[0_16px_40px_rgba(2,23,68,0.35)] md:mb-10">
                  <Box className="relative rounded-2xl bg-gradient-to-br from-[#021744] via-[#04235f] to-[#021744] p-6 text-white md:p-10">
                    <Box className="absolute -right-20 -top-24 h-56 w-56 rounded-full bg-[#42DDD1]/20 blur-3xl" />
                    <Box className="absolute -bottom-20 -left-16 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
                    <Box className="mb-6 flex w-full flex-wrap items-start justify-between gap-4">
                      <span className="inline-flex rounded-full border border-[#42DDD1] bg-[#42DDD1] px-3 py-1 text-xs font-semibold text-[#021744] shadow-sm">
                        {usecaseData.ContentType || 'Use Case Spotlight'}
                      </span>
                      <Box className="flex flex-wrap gap-2">
                        {description ? (
                          <button
                            type="button"
                            onClick={() => scrollToSection(bodySectionRef)}
                            className="rounded-full border border-white/40 bg-white/10 px-3 py-1 text-xs text-white transition-colors duration-200 hover:bg-[#42DDD1] hover:text-[#021744]"
                          >
                            Overview
                          </button>
                        ) : null}
                        {usecaseData.Product || usecaseData.LOB || usecaseData.Area ? (
                          <button
                            type="button"
                            onClick={() => scrollToSection(productSectionRef)}
                            className="rounded-full border border-white/40 bg-white/10 px-3 py-1 text-xs text-white transition-colors duration-200 hover:bg-[#42DDD1] hover:text-[#021744]"
                          >
                            Product
                          </button>
                        ) : null}
                      </Box>
                    </Box>

                    <h1 className="mb-4 text-left text-[26px] font-medium leading-[1.2] tracking-normal md:text-[34px]">
                      {title}
                    </h1>
                    {description ? (
                      <p className="mb-4 text-left leading-7 text-[#d6e6ff]">{description}</p>
                    ) : null}
                    {graphicSrc ? (
                      <Box className="relative mt-6 overflow-hidden rounded-xl border border-white/20">
                        <img
                          src={graphicSrc}
                          alt={title}
                          className="max-h-[360px] w-full object-cover"
                          crossOrigin="anonymous"
                        />
                      </Box>
                    ) : null}
                  </Box>
                </Box>

                <Grid2 container spacing={2} className="mb-8 items-stretch">
                  <MetaCard label="Role">{usecaseData.Role}</MetaCard>
                  <MetaCard label="Job at Hand">{description}</MetaCard>
                  <MetaCard label="Line of Business">{usecaseData.LOB}</MetaCard>
                </Grid2>

                {body ? (
                  <Box
                    ref={bodySectionRef}
                    className="mb-4 rounded-xl border border-[#E6EAF2] bg-white p-6 shadow-sm"
                  >
                    {hasHtmlBody ? (
                      <div
                        className={USECASE_BODY_CLASS}
                        dangerouslySetInnerHTML={{ __html: body }}
                      />
                    ) : (
                      <p className="whitespace-pre-line text-left leading-7 text-[#42506a]">
                        {body}
                      </p>
                    )}
                  </Box>
                ) : null}

                {(usecaseData.Product || usecaseData.LOB || areaTags.length > 0) && (
                  <Grid2
                    ref={productSectionRef}
                    container
                    spacing={2}
                    className="mb-4 items-stretch"
                  >
                    {usecaseData.Product ? (
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <Box className="h-full rounded-xl border border-[#021744] bg-gradient-to-br from-[#021744] to-[#0c2f74] p-5 text-white shadow-sm">
                          <p className="mb-2 text-xs text-[#42DDD1]">Product Used</p>
                          <p>{usecaseData.Product}</p>
                        </Box>
                      </Grid2>
                    ) : null}
                    {usecaseData.LOB ? (
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <Box className="h-full rounded-xl border border-[#021744] bg-gradient-to-br from-[#021744] to-[#0c2f74] p-5 text-white shadow-sm">
                          <p className="mb-2 text-xs text-[#42DDD1]">LOBs</p>
                          <p>{usecaseData.LOB}</p>
                        </Box>
                      </Grid2>
                    ) : null}
                    {areaTags.length > 0 ? (
                      <Grid2 size={{ xs: 12, md: 4 }}>
                        <Box className="h-full rounded-xl border border-[#021744] bg-gradient-to-br from-[#021744] to-[#0c2f74] p-5 text-white shadow-sm">
                          <p className="mb-2 text-xs text-[#42DDD1]">Area</p>
                          <Box className="flex flex-wrap gap-2">
                            {areaTags.map((tag) => (
                              <span
                                key={tag}
                                className="rounded-full bg-white/10 px-3 py-1 text-xs"
                              >
                                {tag}
                              </span>
                            ))}
                          </Box>
                        </Box>
                      </Grid2>
                    ) : null}
                  </Grid2>
                )}

                {usecaseData.CustomerQuote ? (
                  <Box className="mb-4 rounded-xl border border-[#CCEDE9] bg-gradient-to-br from-[#f4fffd] to-white p-6 shadow-sm">
                    <h3 className="mb-3 text-[22px] font-medium text-[#021744]">
                      Customer Perspective
                    </h3>
                    <p className="text-left leading-7 text-[#42506a] italic">
                      &ldquo;{usecaseData.CustomerQuote}&rdquo;
                    </p>
                    {(usecaseData.CustomerTitle || usecaseData.CustomerCompany) && (
                      <p className="mt-3 text-left text-sm font-medium text-[#294b78]">
                        {[usecaseData.CustomerTitle, usecaseData.CustomerCompany]
                          .filter(Boolean)
                          .join(', ')}
                      </p>
                    )}
                  </Box>
                ) : null}

                {(usecaseData.Author ||
                  usecaseData.AuthorTitle ||
                  usecaseData.AuthorCompany) && (
                  <Box className="rounded-xl border border-[#E6EAF2] bg-white p-6 shadow-sm">
                    <h3 className="mb-3 text-[22px] font-medium text-[#021744]">Author</h3>
                    <p className="text-left text-[#42506a]">
                      {[usecaseData.Author, usecaseData.AuthorTitle, usecaseData.AuthorCompany]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </Box>
                )}
              </Container>
            </article>
            <div className="w-full bg-[#F3F4F4] py-12">
              <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
                <div className="flex w-full flex-wrap items-center justify-between gap-4">
                  <Link to="/articles/usecases">
                    <button
                      type="button"
                      className="rounded-full border border-[#021744] px-8 py-4 text-[18px] font-medium leading-6 hover:bg-[#021744] hover:text-white"
                    >
                      View all Usecases
                    </button>
                  </Link>
                  <button
                    type="button"
                    onClick={() => setIsOpenContact(true)}
                    className="rounded-full border border-[#0D131E] px-14 py-4 text-[18px] font-medium leading-6 transition-colors duration-300 hover:bg-[#0D131E] hover:text-white"
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        <ContactUsPopup open={isOpenContact} handleClose={() => setIsOpenContact(false)} />
      </PageMain>
      <Footer />
    </div>
  );
}
